import { SlidersHorizontal } from 'lucide-react'
import { ButtonGlyph, buttonClass, Link, Modal } from '../../../core/index.js'

export type ReleaseMode = 'after_review' | 'at_close' | 'staff'
export type CensusMode = 'automatic' | 'propose'

/** The engine chip's label, from the two settings on Configuration › Automation. */
export function engineLabelOf(releaseMode: ReleaseMode, censusMode: CensusMode): string {
  if (releaseMode === 'staff' && censusMode === 'propose') {
    return 'Engine · proposes'
  }
  return releaseMode === 'staff' || censusMode === 'propose' ? 'Engine · partly automatic' : 'Engine · automatic'
}

interface EngineRow {
  title: string
  alone: boolean
  text: string
}

function rowsOf(releaseMode: ReleaseMode, reviewDays: number, censusMode: CensusMode): EngineRow[] {
  return [
    {
      title: 'Releasing the month',
      alone: releaseMode !== 'staff',
      text:
        releaseMode === 'after_review'
          ? `It releases the month ${reviewDays} day${reviewDays === 1 ? '' : 's'} after bidding closes, so you can review it first. Hold it on the Month tab if it is not ready.`
          : releaseMode === 'at_close'
            ? 'It releases the month as soon as bidding closes.'
            : 'You release the month yourself on the Month tab.',
    },
    {
      title: 'When the census changes',
      alone: censusMode === 'automatic',
      text: censusMode === 'automatic' ? 'When you record a census, it releases or adds shifts to match it, and lists what it did under What the engine did.' : 'When you record a census, it suggests the change under Needs you and waits for you to apply it.',
    },
    {
      title: 'Open shifts',
      alone: true,
      text: 'It always offers open shifts to eligible workers, then to everyone first come. A shift nobody takes close to its start comes to Needs you.',
    },
  ]
}

export interface EngineModeModalProps {
  releaseMode: ReleaseMode
  reviewDays: number
  censusMode: CensusMode
  /** The facility's Configuration page. */
  configHref: string
  onClose: () => void
}

/**
 * What the Today tab's engine chip means, in plain words: what the engine does on its own at this facility and what
 * waits for a person, with the way to Configuration › Automation, where it is changed.
 */
export function EngineModeModal({ releaseMode, reviewDays, censusMode, configHref, onClose }: EngineModeModalProps) {
  const rows = rowsOf(releaseMode, reviewDays, censusMode)
  const alone = rows.every((row) => row.alone)
  const proposesOnly = rows.filter((row) => row.alone).length === 1
  return (
    <Modal
      eyebrow="Today"
      title={engineLabelOf(releaseMode, censusMode)}
      subtitle={alone ? 'The engine runs the routine work on its own. Needs you lists only what it may not decide.' : proposesOnly ? 'The engine suggests; you decide. Most changes wait for you under Needs you.' : 'The engine does some of the work on its own and leaves the rest to you.'}
      onClose={onClose}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          <button type="button" className="text-xs font-bold text-ink-mid hover:text-ink" onClick={onClose}>
            Close
          </button>
          <Link to={`${configHref}?tab=automation`} className={buttonClass({ size: 'sm' })} onClick={onClose}>
            <ButtonGlyph icon={SlidersHorizontal} size="sm" />
            Change on Configuration › Automation
          </Link>
        </div>
      }
    >
      <ul className="flex flex-col divide-y divide-marine/10 px-6 py-2">
        {rows.map((row) => (
          <li key={row.title} className="flex flex-col gap-1 py-3">
            <span className="flex items-center gap-2">
              <span className="text-sm font-black text-ink">{row.title}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${row.alone ? 'bg-green-lt text-green' : 'bg-marine-lt text-marine-dk'}`}>{row.alone ? 'On its own' : 'Waits for you'}</span>
            </span>
            <span className="text-xs leading-relaxed text-ink-mid">{row.text}</span>
          </li>
        ))}
      </ul>
    </Modal>
  )
}
